import { hasSdk } from "./clientTypes";
import { REALM_URL, type WireUpClient } from "./wireUpPrompt";

// The manual way of wiring a client up: the steps a developer follows by hand,
// with the client's own values filled in. With an SDK for the client's stack
// they follow the SDK (docs/reference/authentication-sdk.md); without one
// they build the configuration themselves against the realm's standard
// OpenID Connect endpoints. Keep the steps and that reference in step.

export interface ManualStep {
  title: string;
  /** What to do, in a sentence or two. */
  text?: string;
  /** A snippet to copy. */
  code?: string;
}

const NUGET_SOURCE =
  "The packages are on GitHub Packages. Add https://nuget.pkg.github.com/intility/index.json as a NuGet source and sign in with a personal access token that has read:packages and is SSO-authorized for the intility organization. Use fixed versions, and never commit the token.";

function dotnetEnvironment(client: WireUpClient): string {
  return client.environment === "production"
    ? "AuthenticationEnvironment.Production"
    : "AuthenticationEnvironment.Development";
}

function credentialCode(client: WireUpClient): string {
  return client.authMethod === "secret"
    ? 'Credential.ClientSecret(builder.Configuration["Intility:ClientSecret"]!)'
    : "new SpiffeCredential()";
}

function outboundCode(client: WireUpClient): string {
  return `builder.Services.AddIntilityServiceIdentity(o =>
{
    o.Environment = ${dotnetEnvironment(client)};
    o.ClientId    = "${client.clientId}";
    o.Credential  = ${credentialCode(client)};
});`;
}

// What a client secret or a SPIFFE identity needs besides the code. The
// spiffe step gains a warning when no SDK does the token exchange.
function credentialStep(client: WireUpClient, sdk: boolean): ManualStep | null {
  if (client.authMethod === "spiffe") {
    return {
      title: "Give the workload its SPIFFE identity",
      text: `No secret is used. The pod needs a named service account (not default), the label spiffe.io/spiffe-id: "true", and a CSI volume (driver csi.spiffe.io, read-only) mounted at /run/spire/sockets. Its namespace needs the labels spiffe.io/spiffe-id: "true" and pod-security privileged. Then set the namespace and service account in the Workload identity section of this client: it cannot get a token until you do. The platform team grants the spire-workload-scc to the service account.${sdk ? "" : " The token exchange from the SVID is what the .NET SDK does; without an SDK, ask the Intility ID team before you build that exchange by hand."}`,
    };
  }
  if (client.authMethod === "secret") {
    return {
      title: "Keep the client secret out of the code",
      text: "Read the secret from configuration (user secrets locally, an environment variable or your secret store when deployed). Get it from this client's page in the Intility ID portal, and never hard-code, print or commit it. Rotate it there if it ever leaks.",
    };
  }
  return null;
}

export function buildManualSteps(client: WireUpClient): ManualStep[] {
  // A custom client is managed by the Intility ID team whatever its stack;
  // every other client without an SDK for its stack builds the configuration
  // itself.
  if (client.appType !== "custom" && !hasSdk(client.stack, client.appType)) {
    return buildItYourselfSteps(client);
  }
  switch (client.appType) {
    case "spa":
      return [
        {
          title: "Install the SDK",
          text: "The @intility scope is served from GitHub Packages (https://npm.pkg.github.com). Add @intility:registry=https://npm.pkg.github.com to the project's .npmrc and sign in with a personal access token that has read:packages and is SSO-authorized for the intility organization. Never commit the token.",
          code: "npm install @intility/authentication @intility/authentication-react",
        },
        {
          title: "Create the auth instance once",
          text: "At module scope, never inside a component: a component would create a new Keycloak instance on every render. The environment is required and has no default.",
          code: `import { createAuth } from "@intility/authentication";

export const auth = createAuth({
  environment: "${client.environment}",
  clientId: "${client.clientId}",
});`,
        },
        {
          title: "Wrap your platform",
          text: "Put the provider around the root of the platform, with a fallback shown while the login starts.",
          code: `import { AuthProvider } from "@intility/authentication-react";

<AuthProvider auth={auth} fallback={<Spinner />}>
  <App />
</AuthProvider>`,
        },
        {
          title: "Use the login in your components",
          text: "useAuth() returns the auth instance, whether the user is signed in, and an error if the login could not start (usually a wrong client ID or an unreachable realm).",
          code: `import { useAuth } from "@intility/authentication-react";

const { auth, authenticated, error } = useAuth();

auth.logout();                  // sign out
auth.keycloak.tokenParsed;      // name, email, sub, ...
await auth.getToken();          // a fresh token for a bearer header`,
        },
        {
          title: "Add your redirect URL",
          text: "Use your dev server's address with a wildcard on the end, for example http://localhost:5173/*, and paste it into the Redirect URL box under Wire up login. Redirect URLs are compared literally, and login fails without any error if yours is missing.",
        },
      ];
    case "api": {
      const steps: ManualStep[] = [
        {
          title: "Install the package",
          text: NUGET_SOURCE,
          code: "dotnet add package Intility.Authentication.AspNetCore",
        },
        {
          title: "Validate incoming tokens",
          text: "This replaces a hand-written AddJwtBearer block. The environment is required and has no default, and the audience is this API's client ID. Make sure authentication and authorization are in the pipeline and that your endpoints are protected.",
          code: `builder.Services.AddIntilityAuthentication(builder.Environment, o =>
{
    o.Environment = ${dotnetEnvironment(client)};
    o.Audience    = "${client.clientId}";
});

app.UseAuthentication();
app.UseAuthorization();`,
        },
        {
          title: "Call other services as yourself (optional)",
          text: "Only if this API calls other services with its own identity. Skip it otherwise.",
          code: outboundCode(client),
        },
      ];
      const extra = credentialStep(client, true);
      return extra ? [...steps, extra] : steps;
    }
    case "worker": {
      const steps: ManualStep[] = [
        {
          title: "Install the package",
          text: `${NUGET_SOURCE}${client.authMethod === "spiffe" ? " The SPIFFE credential is in its own package." : ""}`,
          code:
            client.authMethod === "spiffe"
              ? "dotnet add package Intility.Authentication\ndotnet add package Intility.Authentication.Spiffe"
              : "dotnet add package Intility.Authentication",
        },
        {
          title: "Register the client's credential",
          text: "This client only makes outbound calls. The environment is required and has no default.",
          code: outboundCode(client),
        },
      ];
      const extra = credentialStep(client, true);
      return extra ? [...steps, extra] : steps;
    }
    default:
      return [
        {
          title: "Managed by the Intility ID team",
          text: "A custom client has a non-standard configuration that the Intility ID team manages, so there are no setup steps here. Ask them how it should be wired up.",
        },
      ];
  }
}

// Why there is no SDK lane, as the first step: the recorded stack, or none
// recorded (and where to record it).
function noSdkStep(client: WireUpClient): ManualStep {
  return {
    title: "No SDK for this stack",
    text: client.stack
      ? `Intility ID has an SDK for React (a SPA's login) and .NET (APIs and other services). This client's stack is ${client.stack}, which no SDK covers, so build the configuration yourself with the steps below.`
      : "Intility ID has an SDK for React (a SPA's login) and .NET (APIs and other services). This client has no stack recorded: if its project is one of those, set the Tech stack field on the client (under Object ID) to get the SDK guide. Otherwise build the configuration yourself with the steps below.",
  };
}

// The steps of building the configuration yourself, per kind: standard
// OAuth 2.0 and OpenID Connect against the realm, with the client's own
// values in the snippets.
function buildItYourselfSteps(client: WireUpClient): ManualStep[] {
  const realm = REALM_URL[client.environment];
  const discovery = `${realm}/.well-known/openid-configuration`;
  switch (client.appType) {
    case "spa":
      return [
        noSdkStep(client),
        {
          title: "Use an OpenID Connect library",
          text: "Pick a maintained OpenID Connect library for this stack and use its authorization code flow with PKCE. This client is public: it holds no secret, and none belongs in its code.",
        },
        {
          title: "Point it at the realm",
          text: "The realm is the issuer, and the library reads the endpoints from its discovery document.",
          code: `issuer:    ${realm}
clientId:  ${client.clientId}
discovery: ${discovery}`,
        },
        {
          title: "Add your redirect URL",
          text: "Use your dev server's address with a wildcard on the end, for example http://localhost:5173/*, and paste it into the Redirect URL box under Wire up login. Redirect URLs are compared literally, and login fails without any error if yours is missing.",
        },
      ];
    case "api": {
      const steps: ManualStep[] = [
        noSdkStep(client),
        {
          title: "Validate incoming tokens",
          text: `With a JWT library for this stack. The issuer is the realm, the signing keys are its JSON web key set (the URI is in the discovery document), and the audience is this API's client ID. A token from the other environment is not valid: check the issuer on every token.`,
          code: `issuer:    ${realm}
audience:  ${client.clientId}
discovery: ${discovery}`,
        },
        {
          title: "Call other services as yourself (optional)",
          text: "Only if this API calls other services with its own identity. Get a token with the client credentials grant; the scope names the target API's client ID.",
          code: `grant_type: client_credentials
client_id:  ${client.clientId}
scope:      <target client ID>`,
        },
      ];
      const extra = credentialStep(client, false);
      return extra ? [...steps, extra] : steps;
    }
    case "worker": {
      const steps: ManualStep[] = [
        noSdkStep(client),
        {
          title: "Get a token as the service",
          text:
            client.authMethod === "spiffe"
              ? "This service acts as itself. Its credential is a SPIFFE workload identity: the pod presents its SVID as a client assertion on the token request at the realm's token endpoint. The .NET SDK does this exchange; without an SDK, ask the Intility ID team before you build it by hand."
              : "This service acts as itself. Get a token with the client credentials grant at the realm's token endpoint (the URI is in the discovery document), and send it as the bearer token on calls to other services.",
          code:
            client.authMethod === "spiffe"
              ? undefined
              : `grant_type: client_credentials
client_id:  ${client.clientId}
scope:      <target client ID>`,
        },
      ];
      const extra = credentialStep(client, false);
      return extra ? [...steps, extra] : steps;
    }
    default:
      return [noSdkStep(client)];
  }
}
